import React, { useEffect, useState } from "react";
import { IDog } from "@eagerdog/interfaces";

import { toast } from "src/components/Toast/ToastManager";

import styles from "./DogTiles.module.scss";

interface IProps {
  dogs: IDog[];
  hasProfileAccess: boolean;
  onView(dog: IDog): void;
  onEdit(dog: IDog): void;
  onAddPhoto(dog: IDog): void;
  onArchive(dog: IDog): void;
}

const ageLabel = (dob?: Date | string) => {
  if (!dob) {
    return "Age not set";
  }

  const born = new Date(dob);
  if (Number.isNaN(born.getTime())) {
    return "Age not set";
  }

  const now = new Date();
  let years = now.getFullYear() - born.getFullYear();
  const monthDiff = now.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) {
    years -= 1;
  }
  if (years < 1) {
    return "Under 1 year";
  }
  return years === 1 ? "1 year" : `${years} years`;
};

const sexLabel = (sex?: string) => {
  if (!sex) {
    return "";
  }
  return sex.charAt(0).toUpperCase() + sex.slice(1).toLowerCase();
};

const DogTiles: React.FC<IProps> = ({ dogs, hasProfileAccess, onView, onEdit, onAddPhoto, onArchive }) => {
  const [openId, setOpenId] = useState<string>();

  useEffect(() => {
    if (!openId) {
      return;
    }

    const close = () => setOpenId(undefined);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openId]);

  if (dogs.length === 0) {
    return <div className={styles.empty}>No dogs yet.</div>;
  }

  const shareDog = async (dog: IDog) => {
    const url = `${window.location.origin}/dogs/${dog._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: dog.call_name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.show({
        title: "My Dogs",
        content: `Link to ${dog.call_name} copied.`,
        duration: 4000,
        type: "success",
      });
    } catch {
      // The share sheet can be dismissed without an error worth showing.
    }
  };

  return (
    <div className={styles.grid}>
        {dogs.map((dog) => {
          const details = [sexLabel(dog.sex), ageLabel(dog.dob)].filter(Boolean).join(" · ");
          const menuOpen = openId === dog._id;

          return (
            <article className={styles.card} key={dog._id}>
              <div className={styles.body}>
                <div className={styles.photo}>
                  {dog.image_url ? (
                    <img src={dog.image_url} alt={`${dog.call_name}, ${dog.breed}`} />
                  ) : (
                    <button type="button" className={styles.photoEmpty} onClick={() => onAddPhoto(dog)}>
                      <span className={styles.initial}>{dog.call_name.charAt(0).toUpperCase()}</span>
                      <span className={styles.addPhoto}>Add photo</span>
                    </button>
                  )}
                </div>
                <div className={styles.copy}>
                  <div className={styles.nameRow}>
                    <div className={styles.nameBlock}>
                      <h3>{dog.call_name}</h3>
                      <p className={styles.breed}>{dog.breed}</p>
                    </div>
                    <button
                      type="button"
                      className={styles.more}
                      aria-label={`Actions for ${dog.call_name}`}
                      aria-expanded={menuOpen}
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenId(menuOpen ? undefined : dog._id);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="5" cy="12" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="19" cy="12" r="1.5" />
                      </svg>
                    </button>
                  </div>
                  <div className={styles.details}>{details}</div>
                  <button type="button" className={styles.view} onClick={() => onView(dog)}>
                    View profile
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>
              {menuOpen && (
                <div className={styles.menu} onClick={(event) => event.stopPropagation()}>
                  <button type="button" onClick={() => { setOpenId(undefined); onEdit(dog); }}>Edit profile</button>
                  {hasProfileAccess && (
                    <button type="button" onClick={() => { setOpenId(undefined); shareDog(dog); }}>Share</button>
                  )}
                  <button type="button" className={styles.archive} onClick={() => { setOpenId(undefined); onArchive(dog); }}>Archive dog</button>
                </div>
              )}
            </article>
          );
        })}
    </div>
  );
};

export default DogTiles;
